import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { AnimatePresence, EASE, Reveal, Stagger, StaggerItem, HankoStamp, blurIn, fadeInUp, scaleIn } from './motion';
import { MOAT, useLang, pick } from './content';

export function JpDeepMoat() {
  const lang = useLang();
  const reduce = useReducedMotion();
  const [reg, setReg] = useState(0);
  const active = MOAT.keigo.tabs[reg];

  return (
    <section className="border-t border-ink-100 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 py-16 md:py-20">
        <Reveal variant={blurIn}>
          <div className="max-w-3xl">
            <div className="text-[12px] uppercase tracking-[0.18em] text-accent-600 font-semibold">{pick(MOAT.eyebrow, lang)}</div>
            <h2 className="mt-2 display serif-jp text-3xl md:text-4xl font-semibold tracking-tight text-ink-900 text-balance">
              {pick(MOAT.h2, lang)}
            </h2>
            <p className="mt-4 text-[15px] text-ink-600 leading-relaxed">{pick(MOAT.intro, lang)}</p>
          </div>
        </Reveal>

        {/* Input → line → keigo proof rail */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          <Stagger stagger={0.06} className="lg:col-span-4 flex flex-wrap lg:flex-col gap-2">
            {MOAT.inputs.map((i) => (
              <StaggerItem key={i} variant={fadeInUp}>
                <span className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-[13px] font-medium ${
                  i === 'FAX' ? 'bg-warn-500/10 text-warn-700 border-warn-500/20' : 'bg-paper-50 text-ink-700 border-ink-100'
                }`}>
                  <span className="text-ink-400">▤</span> {i}
                </span>
              </StaggerItem>
            ))}
          </Stagger>

          <div className="lg:col-span-2 flex items-center justify-center">
            <motion.div
              aria-hidden
              className="hidden lg:block h-[2px] w-full rounded"
              style={{ background: 'linear-gradient(90deg, var(--tw-gradient-from,#c2703d), #4f46e5)', transformOrigin: 'left' }}
              initial={reduce ? undefined : { scaleX: 0 }}
              whileInView={reduce ? undefined : { scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: 'easeOut' }}
            />
            <div className="lg:hidden text-ink-300 text-2xl">↓</div>
          </div>

          <Reveal variant={scaleIn} className="lg:col-span-6">
            <div className="card-paper p-5">
              <div className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold mb-3">{pick(MOAT.keigo.label, lang)}</div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {MOAT.keigo.tabs.map((tab, i) => (
                  <button
                    key={tab.key}
                    onClick={() => setReg(i)}
                    className={`text-[12px] px-2.5 py-1 rounded-full border transition ${
                      i === reg ? 'bg-accent-600 text-white border-accent-600' : 'border-ink-200 text-ink-600 hover:bg-ink-50'
                    }`}
                  >
                    {pick(tab.name, lang)}
                  </button>
                ))}
              </div>
              <div className="min-h-[3.5rem] rounded-lg bg-paper-50 border border-ink-100 px-4 py-3">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={active.key}
                    initial={reduce ? undefined : { opacity: 0, y: 8 }}
                    animate={reduce ? undefined : { opacity: 1, y: 0 }}
                    exit={reduce ? undefined : { opacity: 0, y: -8 }}
                    transition={{ duration: 0.28, ease: EASE }}
                    className="serif-jp text-[15px] text-ink-900"
                  >
                    {pick(active.sample, lang)}
                  </motion.p>
                </AnimatePresence>
              </div>
              <div className="mt-2 flex gap-1.5">
                <span className="chip-citation text-[10px]">chunk:2</span>
                <span className="chip-citation text-[10px]">chunk:10</span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* 6 differentiators */}
        <Stagger stagger={0.07} className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {MOAT.differentiators.map((d) => (
            <StaggerItem key={d.title} variant={fadeInUp}>
              <div className="card-paper p-5 h-full relative">
                {d.seal && <HankoStamp size={44} rotate={-8} className="absolute top-3 right-3" />}
                <h3 className="display text-[15.5px] font-semibold text-ink-900 pr-10">{pick(d.title, lang)}</h3>
                <p className="mt-2 text-[13.5px] text-ink-600 leading-relaxed">{pick(d.body, lang)}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
